const post = {
  slug: "founder-log-day-117-the-sound-of-persistence",
  title: "Founder Log Day 117: The Sound of Persistence",
  contentType: "blog",
  excerpt: "We spent 3 days debugging IndexTTS2 on Apple Silicon. Every output sounded like static. The model was perfect the whole time — one line in the save pipeline was silently destroying everything.",
  categories: ["AI", "Founder Log"],
  tags: ["founder-log", "indextts2", "tts", "voice-cloning", "apple-silicon", "debugging", "audio"],
  author: {
    name: "Kiarra Montana",
    email: "team@exohaven.online",
  },
  publishedAt: "2026-02-28",
  status: "published",
  featuredImage: "",
  content: `
# Founder Log Day 117: The Sound of Persistence

Some bugs teach you something about yourself.

---

## The Problem

I wanted voice cloning on my Mac Mini M4. Not cloud API calls at $0.02/second — local, private, mine. IndexTTS2 is one of the best open-source TTS models: zero-shot voice cloning, emotional control, BigVGAN vocoder. Everything I needed.

Except every single output sounded like static.

## Three Days of Wrong Answers

Day one: I blamed MPS. Apple's Metal Performance Shaders must be doing something wrong with the diffusion model, right? Tried CPU mode. Still static.

Day two: I blamed BigVGAN. The vocoder must be outputting garbage. Analyzed the tensors — RMS 0.09, clean waveform, all samples in [-1, 1]. Perfect. But the saved file? 99.96% clipped. Every sample slammed to maximum volume.

I tried VibeVoice (Microsoft's 1.5B TTS). Abandoned it. Tried F5-TTS as a fallback. It worked, but I wanted IndexTTS2. I tried MPS with no classifier-free guidance — it ran but sounded terrible. Tried MPS with full CFG — it OOM killed my Mac.

Day three: I stared at the save line.

## One Line

\`\`\`python
torchaudio.save(output_path, wav.type(torch.int16), sampling_rate)
\`\`\`

That's it. That's the entire bug.

\`torchaudio.save()\` with int16 input data **silently auto-normalizes** the values to fill the entire [-32768, 32767] range. A sample at 5000 becomes 32767. A sample at -5000 becomes -32768. Your carefully shaped waveform becomes a wall of clipped noise.

The model was producing perfect audio on every single run. The save function was destroying it.

## The Fix

\`\`\`python
torchaudio.save(
    output_path,
    (wav / 32767.0).clamp(-1.0, 1.0).float(),
    sampling_rate,
    bits_per_sample=16
)
\`\`\`

Save as float32. Tell torchaudio the bit depth explicitly. Done.

I hit play. Clean voice. Perfect clone. Three days of debugging, solved by changing one line.

## What I Actually Learned

The model was never broken. The pipeline was never broken. The save function — the last thing before the file hits disk — was the silent killer.

**Always verify the saved file, not just the model output.**

This is a metaphor for something, probably. The work is good. The product is good. Sometimes the thing destroying your output is the last step between you and the world seeing it.

## The Numbers

- **CPU mode on M4**: ~80 seconds for 9 seconds of audio
- **RAM usage**: 4.5 GB
- **Quality**: Identical to CUDA
- **Cost**: $0 per generation, forever

Not real-time. Don't care. A 5-minute script takes 45 minutes to generate. I can batch that overnight. What matters is: it's mine, it's local, and it works.

## What's Next

IndexTTS2 is now part of the content pipeline. Three personas, each with their own voice:

- **Ember** — the hypnodomme, low and deliberate
- **Zoey** — warm, nurturing, guiding
- **Acacia** — raw and authentic

Scripts from exoCreate → voice cloning from IndexTTS2 → distribution across platforms. No API costs. No content moderation filters blocking my work. No dependency on services that might decide adult content violates their terms next Tuesday.

Sovereignty isn't a philosophy. It's infrastructure.

---

*We published the full technical guide so nobody else has to waste three days on this:*
- *[How to Install IndexTTS2 on Mac](https://exocreate.online/blog/indextts2-mac-install-guide)*
- *[The torchaudio.save Bug (Deep Dive)](https://exocreate.online/blog/indextts2-apple-silicon-tts-fix)*

*Day 117. The audio works. The pipeline is real. Keep building.*
  `.trim(),
};

export default post;
